<script setup lang="ts">
  import { computed, inject } from 'vue'
  import type { Component } from 'vue'
  import { RadioGroupItem } from 'reka-ui'
  import { cn } from '../../../lib/utils'
  import { RADIO_CARD_GROUP_KEY } from './RadioCardGroup.vue'

  /**
   * One option inside a `<RadioCardGroup>`. Renders the radio-card visual:
   * a circular icon cell on the left, label + description in the middle, a
   * radio dot on the right, and an optional `#footer` slot for conditional
   * content (e.g. a picker revealed when this item is selected).
   *
   * Must be rendered inside a `<RadioCardGroup>`; reads the current value,
   * disabled state, and the shared change handler from the group context.
   */
  const props = defineProps<{
    value: string
    label: string
    description?: string
    icon?: Component
    disabled?: boolean
    dataTestid?: string
  }>()

  defineSlots<{
    footer?: (props: { selected: boolean; disabled: boolean }) => unknown
    icon?: (props: { class: string; selected: boolean }) => unknown
    description?: (props: { selected: boolean }) => unknown
  }>()

  const group = inject(RADIO_CARD_GROUP_KEY, null)
  if (!group) {
    console.warn(
      '[RadioCardGroupItem] must be rendered inside a <RadioCardGroup>'
    )
  }

  const selected = computed(() => group?.modelValue.value === props.value)
  const isDisabled = computed(() => !!props.disabled || !!group?.disabled.value)
</script>

<template>
  <RadioGroupItem
    :value="value"
    :disabled="isDisabled"
    :data-testid="dataTestid"
    as-child
  >
    <label
      :class="
        cn(
          'group/radio-item bg-background relative flex w-full select-none flex-col gap-3 border p-4 text-left outline-none transition-colors',
          'border-input',
          'first:rounded-t-md last:rounded-b-md',
          '[&:not(:first-child)]:-mt-px',
          'data-[state=checked]:border-primary data-[state=checked]:bg-muted/20 data-[state=checked]:z-10',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1',
          isDisabled
            ? 'cursor-not-allowed opacity-60'
            : 'hover:bg-muted/40 cursor-pointer'
        )
      "
      :data-state="selected ? 'checked' : 'unchecked'"
      :data-disabled="isDisabled || undefined"
    >
      <div class="flex w-full items-center gap-3.5">
        <div
          :class="
            cn(
              'shadow-xs ring-border flex size-10 shrink-0 items-center justify-center rounded-full ring-1 ring-inset',
              'bg-background',
              'group-data-[state=checked]/radio-item:bg-primary group-data-[state=checked]/radio-item:ring-primary'
            )
          "
        >
          <slot
            name="icon"
            :selected="selected"
            :class="
              cn(
                'size-5 shrink-0',
                'text-muted-foreground',
                'group-data-[state=checked]/radio-item:text-primary-foreground'
              )
            "
          >
            <component
              :is="icon"
              v-if="icon"
              :class="
                cn(
                  'size-5 shrink-0',
                  'text-muted-foreground',
                  'group-data-[state=checked]/radio-item:text-primary-foreground'
                )
              "
            />
          </slot>
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <span class="text-sm font-medium leading-none">{{ label }}</span>
          <span
            v-if="$slots.description || description"
            class="text-muted-foreground text-xs leading-snug"
          >
            <slot name="description" :selected="selected">
              {{ description }}
            </slot>
          </span>
        </div>

        <span
          :class="
            cn(
              'relative ml-auto flex size-5 shrink-0 items-center justify-center rounded-full border',
              'border-input bg-background',
              'group-data-[state=checked]/radio-item:border-primary'
            )
          "
        >
          <span
            v-if="selected"
            class="bg-primary block size-2.5 rounded-full"
          />
        </span>
      </div>

      <div v-if="$slots.footer" class="w-full" @click.stop @keydown.stop>
        <slot name="footer" :selected="selected" :disabled="isDisabled" />
      </div>
    </label>
  </RadioGroupItem>
</template>
